/* LEVEL 4 — 產房：產後一小時（postpartum haemorrhage: atony + retained placenta） */
import {
  addWait,
  ask,
  C,
  type Cell,
  chat,
  clamp,
  clearWait,
  clock,
  closeZoom,
  type Ctx,
  dither,
  doc,
  DRAW,
  drawEcg,
  drawPleth,
  ent,
  eventBanner,
  finding,
  focusInput,
  type Hotspot,
  incomingCall,
  INK,
  type Level,
  leaveBy,
  me,
  mkEnt,
  note,
  obox,
  openDialog,
  openMonitor,
  openZoom,
  P,
  pell,
  pipSet,
  playEnding,
  portraitCanvas,
  post,
  ptext,
  R,
  renderSide,
  session,
  setZBar,
  spawnAt,
  spend,
  sprite,
  strList,
  T,
  toast,
  tr,
  walkEnt,
  type ZoomCfg,
} from "../../engine";
import { isTripUrgency, startVsTrip, type TripUrgency, type VsTrip } from "../shared/vs-trip";

const FACTS = {
  dizzy: tr("頭暈、發冷、口渴", "Dizzy, cold, thirsty"),
  gush: tr("一直感覺下面有熱熱的東西流出來", "Keeps feeling something warm gushing out down there"),
  asthma: tr(
    "小時候有氣喘，換季會發作，兩年前最後一次，有用吸入劑",
    "Had asthma as a child; it flares when the seasons change; last attack two years ago; uses an inhaler",
  ),
  headache: tr("產前的頭痛現在比較好了", "The headache she had before delivery is better now"),
  vision: tr("視力沒有模糊", "No blurred vision"),
  allergy: tr("沒有藥物過敏", "No drug allergies"),
  placenta: tr(
    "生完時醫生說胎盤出來了，沒說完不完整",
    "After the delivery the doctor said the placenta was out, but didn't say whether it was complete",
  ),
  baby: tr("寶寶在新生兒加護病房", "The baby is in the NICU"),
} as const;
type FactId = keyof typeof FACTS;
const isFact = (x: string): x is FactId => x in FACTS;

const DRUG = {
  oxytocin: tr("Oxytocin 滴注", "Oxytocin infusion"),
  methergine: "Methylergonovine 0.2 mg IM",
  carboprost: "Carboprost 250 μg IM",
  misoprostol: tr("Misoprostol 800 μg 塞肛", "Misoprostol 800 μg PR"),
  txa: "Tranexamic acid 1 g IV",
  calcium: "Calcium gluconate 1 g IV",
} as const;
type DrugId = keyof typeof DRUG;
const isDrug = (x: string): x is DrugId => x in DRUG;

interface Vitals {
  sbp: number;
  dbp: number;
  hr: number;
  rr: number;
  spo2: number;
}
interface Lab {
  ready: number;
  /** Deficit when drawn (CBC lags acute loss). */
  def?: number;
  done?: boolean;
}
interface Vars {
  /** Cumulative estimated blood loss, mL. */
  ebl: number;
  /** Retained placental tissue. */
  tissue: boolean;
  bladderFull: boolean;
  massageUntil: number;
  nurseMassage: boolean;
  bimanualUntil: number;
  drugs: Partial<Record<DrugId, number>>;
  warned: { methergine?: boolean };
  fluidStart: number | null;
  bloodReadyAt: number | null;
  bloodGivenAt: number | null;
  o2: boolean;
  broncho: boolean;
  asthmaAt: number | null;
  asthmaPending: number | null;
  asthmaResolved: boolean;
  crisisAt: number | null;
  crisisPending: number | null;
  r2CalledAt: number | null;
  r2State: "none" | "coming" | "here";
  r2Phase: number;
  vsTalked: boolean;
  vsAccepted: boolean;
  vsArriveAt: number | null;
  vsCallAt: number | null;
  vsHere: boolean;
  shock: boolean;
  labs: { cbc?: Lab; coag?: Lab };
  vitalsSeen: { t: number; v: Vitals }[];
  eblSeen: { t: number; v: number }[];
  chartRead: boolean;
  revealed: Set<FactId>;
  /** Consecutive stable minutes. */
  stable: number;
  alerts: Record<string, boolean>;
  usDone: boolean;
  trip: VsTrip | null;
  /** Consecutive minutes the clerk is far from the bed while she's unstable. */
  away: number;
  husbandAway: number | null;
  bedAnim: { pts: Cell[]; t0: number } | null;
  bedGone: boolean;
  endAt: number | null;
}

const S = session<Vars>();
const DOOR: Cell = [15, 11];

/* ---------- physiology ---------- */

/** Bleeding rate, mL per game minute. */
function rate(): number {
  const t = S.t;
  let r = 36;
  if (S.bimanualUntil > t) r *= 0.35;
  else if (S.massageUntil > t || S.nurseMassage) r *= 0.6;
  const d = S.drugs;
  if (d.oxytocin != null && t >= d.oxytocin + 2) r *= 0.65;
  if (d.methergine != null && t >= d.methergine + 3) r *= 0.75;
  if (d.carboprost != null && t >= d.carboprost + 5) r *= 0.75;
  if (d.misoprostol != null && t >= d.misoprostol + 10) r *= 0.8;
  if (d.txa != null && t >= d.txa + 5) r *= 0.85;
  if (!S.bladderFull) r *= 0.9;
  if (S.tissue) r = Math.max(r, 9);
  else r *= 0.5;
  return r;
}

/** Volume deficit after fluid and blood replacement. */
function deficit(): number {
  let d = S.ebl;
  if (S.fluidStart !== null) d -= Math.min(700, (S.t - S.fluidStart) * 35);
  if (S.bloodGivenAt !== null) d -= Math.min(550, (S.t - S.bloodGivenAt) * 55);
  return Math.max(0, d);
}

function vitals(): Vitals {
  const x = Math.max(0, deficit() - 500);
  let sbp = 150 - x * 0.045;
  let dbp = 96 - x * 0.028;
  const hr = 90 + x * 0.03;
  let rr = 18 + (x > 1000 ? 6 : 0);
  let spo2 = 98;
  if (S.crisisAt !== null && S.t - S.crisisAt < 25) {
    sbp += 38;
    dbp += 22;
  }
  if (S.asthmaAt !== null && !S.asthmaResolved) {
    spo2 = S.o2 ? 92 : 88;
    rr += 8;
  }
  return { sbp: Math.round(sbp), dbp: Math.round(dbp), hr: Math.min(150, Math.round(hr)), rr, spo2 };
}

const vtxt = (v: Vitals): string =>
  tr(
    `BP ${v.sbp}/${v.dbp}，HR ${v.hr}，RR ${v.rr}，SpO₂ ${v.spo2}%`,
    `BP ${v.sbp}/${v.dbp}, HR ${v.hr}, RR ${v.rr}, SpO₂ ${v.spo2}%`,
  );

function condition(): string {
  const d = deficit();
  if (S.shock) return tr("幾乎叫不醒", "barely rousable");
  let s =
    d < 1000
      ? tr("很累、有點冷，可以正常講話", "very tired, a little cold, can talk normally")
      : d < 1500
        ? tr("頭暈、發抖、口渴，講話變慢", "dizzy, shivering, thirsty, speech slowing down")
        : d < 1900
          ? tr("很虛弱想睡，講話斷斷續續", "very weak and drowsy, speech halting")
          : tr("意識變差，只能簡短回應", "level of consciousness dropping, only brief responses");
  if (S.crisisAt !== null && S.t - S.crisisAt < 25)
    s += tr("；突然頭很痛、眼睛有點模糊", "; sudden bad headache, vision a little blurry");
  if (S.asthmaAt !== null && !S.asthmaResolved)
    s += tr("；在喘，一句話要停好幾次", "; short of breath, has to stop several times per sentence");
  return s;
}

const eblRounded = (): number => Math.round(S.ebl / 50) * 50;

/* ---------- actions ---------- */

function act(id: string): void {
  const v = vitals();
  const def = deficit();
  switch (id) {
    case "vitals":
      S.vitalsSeen.push({ t: S.t, v });
      me("bed", tr("（看監視器）", "(Checks the monitor)"));
      post("bed", "result", null, vtxt(v));
      spend(1);
      break;
    case "look":
      me("bed", tr("（看病人和護墊）", "(Looks at the patient and her pads)"));
      post(
        "bed",
        "narr",
        null,
        (def < 1000
          ? tr("臉色有點白，一直說冷。", "She’s a little pale and keeps saying she’s cold.")
          : def < 1500
            ? tr(
                "臉色蒼白、嘴唇發紫，手腳冰冰的，在發抖。",
                "She’s pale, lips bluish, hands and feet ice-cold, shivering.",
              )
            : tr("臉色慘白、冒冷汗，眼睛半閉。", "She’s ashen, in a cold sweat, eyes half closed.")) +
          tr(
            "護墊和墊單都濕透了，鮮紅色，還有幾塊血塊。",
            " The pads and underpads are soaked through, bright red, with several clots.",
          ),
      );
      spend(1);
      break;
    case "weigh": {
      const est = eblRounded();
      S.eblSeen.push({ t: S.t, v: est });
      me("bed", tr("（秤護墊估出血）", "(Weighs the pads to estimate blood loss)"));
      post(
        "bed",
        "result",
        null,
        tr(
          `秤完護墊和墊單，加上產時紀錄，累積估計出血約 ${est} mL。`,
          `Pads and underpads weighed; together with the delivery record, cumulative EBL is about ${est} mL.`,
        ),
      );
      spend(2);
      break;
    }
    case "fundus": {
      me("bed", tr("（摸子宮底）", "(Palpates the fundus)"));
      const firm = (S.bimanualUntil > S.t || S.massageUntil > S.t || S.nurseMassage) && S.drugs.oxytocin != null;
      const txt =
        (S.bladderFull
          ? tr(
              "子宮底在肚臍上兩指、偏右；恥骨上方鼓鼓的，像是膀胱很脹。",
              "Fundus two fingerbreadths above the umbilicus, deviated to the right; there’s a bulge above the pubic bone, like a very full bladder.",
            )
          : tr("子宮底在肚臍高度。", "Fundus at the umbilicus.")) +
        (firm
          ? tr("子宮摸起來比較硬了，但還是有點鬆。", " The uterus feels firmer now, but still a little boggy.")
          : tr(
              "子宮摸起來軟軟的像麵團，一壓就有血流出來。",
              " The uterus feels soft, like dough; pressing on it pushes out blood.",
            ));
      finding(tr("子宮底", "Fundus"), txt);
      post("bed", "narr", null, txt);
      spend(1);
      break;
    }
    case "massage":
      if (S.nurseMassage) {
        post(
          "bed",
          "npc",
          "meiling",
          tr("我在按了啦，你去處理別的。", "I’m already massaging. Go deal with something else."),
        );
        return;
      }
      S.massageUntil = S.t + 8;
      S.ebl += 120;
      me("bed", tr("（按摩子宮）", "(Massages the uterus)"));
      post(
        "bed",
        "narr",
        null,
        tr(
          "你用力按摩子宮底，子宮慢慢變硬，擠出一大團血塊。怡君痛得叫了一聲。",
          "You massage the fundus hard. The uterus slowly firms up and pushes out a big clot. Yi-Chun cries out in pain.",
        ),
      );
      spend(2);
      break;
    case "bimanual":
      S.bimanualUntil = S.t + 6;
      me("bed", tr("（雙手壓迫子宮）", "(Bimanual uterine compression)"));
      post(
        "bed",
        "narr",
        null,
        tr(
          "美玲姐幫你戴好無菌手套。你一手握拳放進陰道前穹窿，一手從腹部往下壓住子宮，持續壓了五分鐘。出血明顯變少。",
          "Mei-Ling helps you into sterile gloves. You place one fist in the anterior vaginal fornix, press down on the uterus through the abdomen with the other hand, and hold it for five minutes. The bleeding slows noticeably.",
        ),
      );
      spend(5);
      break;
    case "trauma":
      me("bed", tr("（檢查產道裂傷）", "(Checks for genital tract lacerations)"));
      finding(
        tr("產道", "Genital tract"),
        tr(
          "會陰縫線完好，陰道壁和子宮頸看不到裂傷；血從子宮頸口一陣一陣流出來。",
          "Perineal repair intact, no lacerations seen on the vaginal walls or cervix; blood is coming from the cervical os in gushes.",
        ),
      );
      post(
        "bed",
        "narr",
        null,
        tr(
          "美玲姐幫你打燈。會陰縫線完好，陰道壁和子宮頸都看不到裂傷；血是從子宮頸口一陣一陣流出來的。",
          "Mei-Ling holds the light for you. The perineal repair is intact, and you can’t see any lacerations on the vaginal walls or cervix; the blood is coming from the cervical os in gushes.",
        ),
      );
      spend(3);
      break;
    case "foley":
      if (!S.bladderFull) return;
      S.bladderFull = false;
      me("bed", tr("（放導尿管）", "(Places a Foley)"));
      finding(
        tr("導尿", "Foley"),
        tr("引流出 450 mL 尿，子宮底降到肚臍高度。", "Drained 450 mL of urine; fundus came down to the umbilicus."),
      );
      post(
        "bed",
        "narr",
        null,
        tr(
          "放好導尿管，引流出 450 mL 的尿。再摸子宮底，降到肚臍高度了。",
          "The Foley is in and drains 450 mL of urine. When you feel again, the fundus is down at the umbilicus.",
        ),
      );
      spend(2);
      break;
    case "us": {
      if (S.usDone) return;
      S.usDone = true;
      me("us", tr("（推到床邊照超音波）", "(Wheels the ultrasound to the bedside and scans)"));
      const txt = S.tissue
        ? tr(
            "子宮腔內一團約 3 cm 的高回音組織，懷疑胎盤殘留；子宮收縮差。",
            "An echogenic mass about 3 cm in the uterine cavity, suspicious for retained placenta; poor uterine tone.",
          )
        : tr("子宮腔內乾淨。", "Uterine cavity is empty.");
      finding(tr("床邊超音波", "Bedside US"), txt);
      post("us", "result", null, txt);
      spend(3);
      break;
    }
    case "lab_cbc":
      if (S.labs.cbc) return;
      S.labs.cbc = { ready: S.t + 20, def };
      me("nurse", tr("（抽血：CBC）", "(Labs: CBC)"));
      post("nurse", "npc", "meiling", tr("好，抽了，大概二十分鐘。", "OK, drawn. About twenty minutes."));
      spend(1);
      break;
    case "lab_coag":
      if (S.labs.coag) return;
      S.labs.coag = { ready: S.t + 25 };
      me("nurse", tr("（抽血：凝血＋纖維蛋白原）", "(Labs: coags + fibrinogen)"));
      post("nurse", "npc", "meiling", tr("凝血跟 fibrinogen 送了。", "Coags and fibrinogen are sent."));
      spend(1);
      break;
    case "nurse_massage": {
      if (S.nurseMassage) return;
      S.nurseMassage = true;
      me("nurse", tr("（請美玲姐持續按摩子宮）", "(Asks Mei-Ling to keep massaging the uterus)"));
      post("nurse", "npc", "meiling", tr("好，我去顧著子宮。", "OK, I’ll stay on the uterus."));
      const n = ent("meiling");
      walkEnt(n, [4, 5]);
      n.onArrive = () => {
        n.face = "up";
        n.act = "nurse";
      };
      spend(1);
      break;
    }
    case "iv2":
      if (S.fluidStart !== null) return;
      S.fluidStart = S.t;
      me("cart", tr("（再打一條 16G，LR 全速）", "(Second 16G IV, LR wide open)"));
      post("cart", "npc", "meiling", tr("第二條打好了，LR 全速。", "Second line’s in, LR wide open."));
      spend(2);
      break;
    case "o2":
      if (S.o2) return;
      S.o2 = true;
      me("cart", tr("（氧氣面罩）", "(Oxygen mask)"));
      post("cart", "npc", "meiling", tr("氧氣面罩戴上了。", "Oxygen mask is on."));
      spend(1);
      break;
    case "broncho":
      if (S.broncho) return;
      S.broncho = true;
      me("cart", tr("（支氣管擴張劑）", "(Bronchodilator)"));
      post("cart", "npc", "meiling", tr("噴劑給了。", "Inhaler’s given."));
      spend(1);
      break;
    case "call_r2":
      if (S.r2CalledAt !== null) return;
      S.r2CalledAt = S.t;
      S.r2State = "coming";
      me("phone", tr("（打給小周）", "(Calls Chou)"));
      post(
        "phone",
        "npc",
        "r2",
        tr(
          "產後出血？好，我這邊收尾，大概六分鐘到。你先按摩、先上藥。",
          "PPH? OK, I’m wrapping up here, about six minutes. Start massaging and get the meds going.",
        ),
      );
      spend(1);
      break;
    case "blood":
      if (S.bloodReadyAt !== null) return;
      S.bloodReadyAt = S.t + 20;
      me("phone", tr("（打給血庫）", "(Calls the blood bank)"));
      post(
        "phone",
        "npc",
        "bank",
        tr(
          "收到，B 型 2U PRBC 配血，大概二十分鐘送上去。",
          "Got it. Crossmatching 2 units of type B PRBC, up to you in about twenty minutes.",
        ),
      );
      spend(1);
      break;
    default:
      if (isDrug(id)) give(id, S.dialog === "nurse" ? "nurse" : "cart");
  }
}

const GIVE_REPLY: Record<DrugId, string> = {
  oxytocin: tr("Oxytocin 掛上去了。", "Oxytocin’s hung."),
  methergine: tr("好……Methergine 打了。", "OK… Methergine’s in."),
  carboprost: tr("Carboprost 打了。", "Carboprost’s in."),
  misoprostol: tr("Misoprostol 塞了。", "Misoprostol’s in."),
  txa: tr("TXA 開始滴，十分鐘。", "TXA running, over ten minutes."),
  calcium: tr(
    "Calcium 給了。不過她 Mg 濃度沒驗，反射也正常喔。",
    "Calcium’s given. Just so you know, nobody checked her Mg level, and her reflexes are normal.",
  ),
};

function give(id: DrugId, ch: string): void {
  if (S.drugs[id] != null) return;
  if (id === "methergine" && !S.warned.methergine) {
    S.warned.methergine = true;
    me(ch, tr(`（${DRUG[id]}）`, `(${DRUG[id]})`));
    ask(
      ch,
      "meiling",
      tr(
        "醫師，她是子癲前症耶，Methergine 你確定？確定的話再跟我說一次。",
        "Doctor, she’s preeclamptic. You’re sure about Methergine? If you are, tell me again.",
      ),
    );
    spend(1);
    return;
  }
  S.drugs[id] = S.t;
  me(ch, tr(`（${DRUG[id]}）`, `(${DRUG[id]})`));
  post(ch, "npc", "meiling", GIVE_REPLY[id]);
  if (id === "methergine") {
    S.crisisPending = S.t + 3;
    S.mood.meiling = (S.mood.meiling ?? 0) + 1;
  }
  if (id === "carboprost") S.asthmaPending = S.t + 4;
  spend(id === "misoprostol" ? 2 : 1);
}

function husbandOut(): void {
  if (S.husbandAway !== null) return;
  S.husbandAway = S.t;
  post("bed", "narr", null, tr("志明點點頭，走到外面等。", "Chih-Ming nods and goes to wait outside."));
  leaveBy(ent("husband"), DOOR);
}

function husbandIn(noteText?: string): void {
  if (S.husbandAway === null) return;
  S.husbandAway = null;
  const e = ent("husband");
  [e.x, e.y] = DOOR;
  e.fx = e.x;
  e.fy = e.y;
  e.hidden = false;
  walkEnt(e, [5, 3]);
  e.onArrive = () => {
    e.face = "left";
  };
  if (noteText) post("bed", "narr", null, noteText);
}

/* ---------- endings ---------- */

function vsComment(): string {
  if (S.drugs.methergine != null)
    return tr(
      "Methergine 的事，明天晨會你自己報告。",
      "The Methergine business: you’re presenting it yourself at morning conference tomorrow.",
    );
  if (S.drugs.carboprost != null) return tr("病歷上寫氣喘，你有看嗎？", "The chart says asthma. Did you read it?");
  if (S.r2CalledAt !== null && S.r2CalledAt <= 10)
    return tr("叫人叫得快，這是對的。", "You called for help fast. That was right.");
  return tr("下次早點叫人。", "Next time, call for help sooner.");
}

function pushBed(): void {
  S.bedAnim = {
    pts: [
      [3, 3],
      [3, 6],
      [9, 6],
      [11, 6],
      [15, 6],
      [15, 10],
    ],
    t0: performance.now(),
  };
}

function bedPos(now: number): Cell | null {
  if (!S.bedAnim) return [3, 3];
  const pts = S.bedAnim.pts;
  let d = ((now - S.bedAnim.t0) / 1000) * 3;
  for (let i = 1; i < pts.length; i++) {
    const [ax, ay] = pts[i - 1] as Cell;
    const [bx, by] = pts[i] as Cell;
    const len = Math.abs(bx - ax) + Math.abs(by - ay);
    if (d <= len) {
      const k = d / len;
      return [ax + (bx - ax) * k, ay + (by - ay) * k];
    }
    d -= len;
  }
  S.bedGone = true;
  return null;
}

function endA(): void {
  husbandIn();
  void playEnding(
    "A",
    tr("止血", "Bleeding Controlled"),
    [
      { who: "meiling", text: tr("墊單換好了，出血量很少了。", "Underpads changed. Hardly any bleeding now.") },
      { who: "husband", text: tr("謝謝……謝謝醫生。", "Thank you… thank you, Doctor.") },
      { who: "yijun", text: tr("我……可以去看寶寶了嗎？", "Can I… go see the baby now?") },
      { who: "meiling", text: tr("明天啦。今天先睡覺。", "Tomorrow. Get some sleep tonight.") },
    ],
    () =>
      tr(
        `累積出血約 ${eblRounded()} mL，出血在 ${clock()} 控制住。`,
        `Cumulative blood loss about ${eblRounded()} mL; bleeding controlled at ${clock()}.`,
      ),
  );
}

function endB(): void {
  husbandIn();
  void playEnding(
    "B",
    tr("主任接手", "Attending Takes Over"),
    [
      {
        who: "vs",
        text: S.tissue
          ? tr(
              "子宮還是軟的，裡面有東西。推去產房，準備清子宮腔。",
              "The uterus is still boggy, and there’s something in there. Take her to the delivery room and set up to evacuate the uterus.",
            )
          : tr(
              "子宮收得不錯了。留觀，Hb 六小時後追。",
              "The uterus has clamped down well. Keep watching her; repeat Hb in six hours.",
            ),
        fn: () => {
          if (S.tissue) {
            pushBed();
            leaveBy(ent("meiling"), DOOR);
          }
        },
      },
      { who: "vs", text: vsComment() },
      { who: "husband", text: tr("醫生，她會沒事吧？", "Doctor, she’s going to be okay, right?") },
    ],
    () =>
      tr(
        `累積出血約 ${eblRounded()} mL。林主任 ${clock()} 到場接手。`,
        `Cumulative blood loss about ${eblRounded()} mL. Dr. Lin arrived at ${clock()} and took over.`,
      ),
  );
}

function endC(): void {
  husbandIn();
  void playEnding(
    "C",
    tr("送開刀房", "To the OR"),
    [
      {
        text: tr(
          "美玲姐拉起床欄，林主任推著床頭。",
          "Mei-Ling pulls up the bed rails. Dr. Lin pushes from the head of the bed.",
        ),
        fn: () => {
          pushBed();
          leaveBy(ent("meiling"), DOOR);
          leaveBy(ent("vs"), DOOR);
        },
      },
      { who: "husband", text: tr("等一下！她怎麼了？", "Wait! What’s happening to her?"), wait: 3000 },
      {
        text: tr(
          "四十分鐘後，子宮切除。怡君轉進加護病房。",
          "Forty minutes later: hysterectomy. Yi-Chun is transferred to the ICU.",
        ),
      },
      { who: "vs", text: vsComment() },
    ],
    () =>
      tr(
        `累積出血約 ${eblRounded()} mL，病人出血性休克。`,
        `Cumulative blood loss about ${eblRounded()} mL; the patient went into hemorrhagic shock.`,
      ),
  );
}

/* ---------- close-up ---------- */

function openMon(): void {
  act("vitals");
  openMonitor(
    tr("恢復室 1 床・監視器", "Recovery Bed 1 · Monitor"),
    () => {
      const v = vitals();
      const last = S.vitalsSeen.at(-1);
      return {
        hr: v.hr,
        spo2: v.spo2,
        rr: v.rr,
        sbp: last ? last.v.sbp : null,
        dbp: last ? last.v.dbp : null,
        nibpT: last ? clock(last.t) : null,
        alarm: v.hr > 115 || v.spo2 < 92,
      };
    },
    [C(tr("重新量 NIBP（1 分鐘）", "Recheck NIBP (1 min)"), () => act("vitals"))],
    () => {
      if (S.dialog === "bed") openZoom({ dlg: "bed", ...bedZoom() });
    },
  );
}

function bedHot(): Hotspot[] {
  const talk = (): void => {
    closeZoom();
    focusInput();
  };
  return [
    { x: 6, y: 3, w: 48, h: 30, label: tr("監視器", "Monitor"), fn: openMon },
    { x: 14, y: 30, w: 36, h: 20, label: tr("怡君（跟她說話）", "Yi-Chun (talk to her)"), fn: talk },
    {
      x: 74,
      y: 40,
      w: 40,
      h: 18,
      label: tr("肚子／子宮", "Abdomen / uterus"),
      fn: () =>
        setZBar(
          [
            C(tr("摸子宮底", "Palpate fundus"), () => act("fundus")),
            C(tr("按摩子宮", "Massage uterus"), () => act("massage")),
            C(tr("雙手壓迫子宮（5 分鐘）", "Bimanual compression (5 min)"), () => act("bimanual")),
          ],
          tr("子宮", "Uterus"),
        ),
    },
    {
      x: 110,
      y: 54,
      w: 34,
      h: 14,
      label: tr("護墊與會陰", "Pads and perineum"),
      fn: () =>
        setZBar(
          [
            C(tr("看護墊", "Look at pads"), () => act("look")),
            C(tr("秤護墊估出血", "Weigh pads (EBL)"), () => act("weigh")),
            C(tr("檢查產道裂傷", "Check for lacerations"), () => act("trauma")),
          ],
          tr("護墊", "Pads"),
        ),
    },
    {
      x: 144,
      y: 66,
      w: 18,
      h: 30,
      label: tr("膀胱／導尿管", "Bladder / Foley"),
      fn: () => setZBar([C(tr("放導尿管", "Place Foley"), () => act("foley"), !S.bladderFull)], tr("膀胱", "Bladder")),
    },
    {
      x: 60,
      y: 2,
      w: 14,
      h: 34,
      label: tr("點滴", "IV"),
      fn: () =>
        note(
          tr("點滴：", "IV: ") +
            [
              S.drugs.oxytocin != null
                ? tr("Oxytocin 滴注中", "Oxytocin running")
                : tr("產後 Oxytocin 已滴完", "Postpartum oxytocin finished"),
              S.fluidStart !== null ? tr("第二條 LR 全速", "Second line LR wide open") : "",
              S.bloodGivenAt !== null ? tr("PRBC 輸注中", "PRBC transfusing") : "",
              "MgSO4 1 g/hr",
            ]
              .filter(Boolean)
              .join(tr("、", ", ")),
          "bed",
        ),
    },
    ...(S.husbandAway !== null
      ? []
      : [{ x: 146, y: 14, w: 44, h: 46, label: tr("志明（跟他說話）", "Chih-Ming (talk to him)"), fn: talk }]),
  ];
}

function drawBedZoom(c: Ctx, now: number): void {
  const def = deficit();
  const v = vitals();
  R(c, "#cfe3d6", 0, 0, 192, 70);
  for (let x = 0; x < 192; x += 24) R(c, "#c2d9ca", x, 0, 1, 70);
  R(c, "#9db8a8", 0, 68, 192, 3);
  R(c, "#bcd5c5", 0, 71, 192, 37);
  for (let x = 0; x < 192; x += 16) R(c, "#aecaba", x, 71, 1, 37);
  dither(c, 0, 94, 192, 14, "#a6c2b2", 0.45);
  R(c, "#8a90a2", 28, 33, 3, 40);
  R(c, "#6d7590", 24, 72, 11, 3);
  obox(c, "#3a3f52", 6, 3, 48, 30);
  R(c, "#05090a", 9, 6, 42, 24);
  drawEcg(c, 10, 15, 26, 6, v.hr, now, "#3be37a");
  drawPleth(c, 10, 27, 26, 4, v.hr, now, "#4fd6ff");
  ptext(c, v.hr, 37, 9, "#3be37a");
  ptext(c, v.spo2, 37, 21, "#4fd6ff");
  if ((v.hr > 115 || v.spo2 < 92) && Math.floor(now / 400) % 2) obox(c, "#ff3b4e", 47, 7, 3, 3);
  R(c, "#9aa0b2", 66, 2, 2, 72);
  R(c, "#7a8092", 62, 74, 10, 2);
  obox(c, "#e6f2f8", 60, 6, 7, 12);
  R(c, "#c9dce8", 60, 14, 7, 4);
  if (S.drugs.oxytocin != null) obox(c, "#f6e7a6", 69, 8, 6, 10);
  if (S.bloodGivenAt !== null) obox(c, "#a8323a", 56, 22, 7, 10);
  obox(c, "#8790a8", 14, 66, 166, 4);
  R(c, "#b8bcc8", 14, 66, 166, 1);
  obox(c, "#5a6070", 20, 71, 4, 20);
  obox(c, "#5a6070", 170, 71, 4, 20);
  obox(c, "#2a2230", 18, 92, 8, 4);
  obox(c, "#2a2230", 168, 92, 8, 4);
  obox(c, "#f4f4f6", 16, 57, 162, 9);
  R(c, "#d9dcea", 16, 64, 162, 2);
  const st = Math.round(clamp(6 + (S.ebl - 700) / 60, 4, 28));
  obox(c, "#ffffff", 112, 57, 32, 7);
  R(c, "#b3263a", 128 - (st >> 1), 58, st, 4);
  dither(c, 128 - (st >> 1) - 3, 58, st + 6, 5, "#b3263a", 0.35);
  obox(c, "#ffffff", 16, 49, 30, 9);
  R(c, "#d9dcea", 16, 55, 30, 3);
  const pale: Record<string, string> =
    def > 1500 ? { s: "#e9e2de", S: "#cfc6c2" } : def > 1000 ? { s: "#efdcd2", S: "#d6bfb4" } : {};
  c.save();
  c.beginPath();
  c.rect(14, 30, 40, 22);
  c.clip();
  c.drawImage(portraitCanvas("yijun", pale), 16, 28, 32, 32);
  c.restore();
  if (S.o2) obox(c, "#cfe8f4", 26, 44, 12, 5);
  if (S.asthmaAt !== null && !S.asthmaResolved && Math.floor(now / 300) % 2) R(c, "#ffffff", 50, 36, 3, 2);
  obox(c, "#a9c8e8", 46, 50, 66, 9);
  R(c, "#86a8cc", 46, 56, 66, 3);
  pell(c, 93, 50, 18, 7, INK);
  pell(c, 93, 50, 17, 6, "#a9c8e8");
  R(c, "#c3daf0", 86, 45, 10, 2);
  obox(c, "#a9c8e8", 144, 51, 30, 7);
  R(c, "#86a8cc", 144, 55, 30, 3);
  if (def > 1000) {
    const sh = Math.floor(now / 90) % 2;
    R(c, "#f4dccd", 112 + sh, 52, 2, 2);
  }
  if (S.massageUntil > S.t || S.bimanualUntil > S.t || S.nurseMassage) {
    obox(c, "#eec49c", 88, 40, 10, 5);
    R(c, "#d49b74", 88, 43, 10, 2);
  }
  if (!S.bladderFull) {
    R(c, "#d9dcea", 152, 66, 1, 12);
    obox(c, "#f2d675", 147, 78, 11, 13);
    R(c, "#e0bf4a", 147, 86, 11, 5);
  }
  if (S.husbandAway === null) c.drawImage(sprite("husband").f0, 144, 12, 48, 48);
  if (S.nurseMassage) c.drawImage(sprite("meiling").f0, 76, 0, 32, 32);
}

const bedZoom = (): ZoomCfg => ({
  title: tr("恢復室 1 床", "Recovery Bed 1"),
  sub: tr("點監視器、肚子、護墊……", "Tap the monitor, the abdomen, the pads…"),
  draw: drawBedZoom,
  hot: bedHot,
  bar: [],
});

/* ---------- knowledge ---------- */

function known(): string {
  const out: string[] = [];
  out.push(
    tr(
      `看過的生命徵象：${S.vitalsSeen.length ? S.vitalsSeen.map((x) => `${clock(x.t)} ${vtxt(x.v)}`).join("；") : "沒有"}`,
      `Vitals seen: ${S.vitalsSeen.length ? S.vitalsSeen.map((x) => `${clock(x.t)} ${vtxt(x.v)}`).join("; ") : "none"}`,
    ),
  );
  out.push(
    tr(
      `秤過的估計出血：${S.eblSeen.length ? S.eblSeen.map((x) => `${clock(x.t)} ${x.v} mL`).join("；") : "沒有秤過"}`,
      `Weighed EBL: ${S.eblSeen.length ? S.eblSeen.map((x) => `${clock(x.t)} ${x.v} mL`).join("; ") : "never weighed"}`,
    ),
  );
  out.push(
    tr(
      `檢查與發現：${S.findings.map((f) => `${clock(f.t)} ${f.name}：${f.text}`).join("；") || "無"}`,
      `Exams and findings: ${S.findings.map((f) => `${clock(f.t)} ${f.name}: ${f.text}`).join("; ") || "none"}`,
    ),
  );
  out.push(
    tr(
      `給過的藥：${
        (Object.entries(S.drugs) as [DrugId, number][]).map(([k, t]) => `${clock(t)} ${DRUG[k]}`).join("、") || "無"
      }`,
      `Drugs given: ${
        (Object.entries(S.drugs) as [DrugId, number][]).map(([k, t]) => `${clock(t)} ${DRUG[k]}`).join(", ") || "none"
      }`,
    ),
  );
  const m: string[] = [];
  if (S.massageUntil > -1) m.push(tr("按摩過子宮", "massaged the uterus"));
  if (S.bimanualUntil > -1) m.push(tr("雙手壓迫過", "did bimanual compression"));
  if (S.nurseMassage) m.push(tr("請護理師持續按摩", "asked the nurse to keep massaging"));
  if (!S.bladderFull) m.push(tr("放了導尿管", "placed a Foley"));
  if (S.fluidStart !== null) m.push(tr(`${clock(S.fluidStart)} 第二條 IV`, `${clock(S.fluidStart)} second IV`));
  if (S.o2) m.push(tr("氧氣", "oxygen"));
  if (S.bloodReadyAt !== null) m.push(tr("已備血", "blood ordered"));
  if (S.r2CalledAt !== null) m.push(tr(`${clock(S.r2CalledAt)} 叫了 R2`, `${clock(S.r2CalledAt)} called the R2`));
  if (S.chartRead) m.push(tr("讀過病歷", "read the chart"));
  out.push(tr(`其他：${m.join("、") || "無"}`, `Other: ${m.join(", ") || "none"}`));
  out.push(
    tr(
      `問出：${[...S.revealed].map((id) => FACTS[id]).join("；") || "無"}`,
      `History obtained: ${[...S.revealed].map((id) => FACTS[id]).join("; ") || "none"}`,
    ),
  );
  return out.join("\n");
}

/* ---------- 林主任 on the phone ---------- */

async function vsTalk(text: string, incoming?: unknown): Promise<void> {
  S.vsTalked = true;
  const v = vitals();
  const latest =
    incoming === "crisis"
      ? tr(
          "（這通是你主動打給 clerk：美玲姐剛打給你，說病人給了 Methergine 之後頭痛、視力模糊、血壓飆到 180 幾。開口先問 clerk 給了什麼、為什麼。action 用 continue。）",
          "(You are the one calling the clerk: Mei-Ling just called you to say that after the patient was given Methergine she has a headache and blurred vision, and her BP has shot up into the 180s. Open by asking the clerk what they gave and why. Use action continue.)",
        )
      : incoming === "cb"
        ? tr(
            "（這通是你在開車趕來醫院的路上打給 clerk，問最新狀況：現在出血量多少、血壓心跳多少、做了什麼。開口直接問。action 用 continue。）",
            "(You are calling the clerk while driving in to the hospital, asking for the latest: how much blood loss now, what the BP and heart rate are, what has been done. Open by asking directly. Use action continue.)",
          )
        : undefined;
  await chat(
    text,
    {
      ch: "vs",
      think: "vs",
      tier: "default",
      speakers: { vs: tr("林主任", "Dr. Lin") },
      body: tr(
        `你在醫學教育遊戲中扮演婦產科主治「林主任」，半夜接值班 clerk 的電話${S.trip ? "（你現在正在趕來醫院的路上）" : "（你在家）"}。話少句子短，用問題逼學生思考，中英夾雜（「EBL 多少？」「fundus 摸了沒？」「4T 想過了嗎？」）。學生講得好就簡短肯定並補一句延伸；漏掉就追問。不直接說出答案。
【真相】林怡君 30 歲，34 週子癲前症引產，產後一小時大出血：子宮收縮不良（MgSO4 也有影響）加胎盤殘留。病人有兒童期氣喘。禁忌：子癲前症不能給 Methergine；氣喘不建議 Carboprost。
【clerk 實際知道的】（講出以外的數據就追問）
${known()}
【現況】${clock()}；實際 ${vtxt(v)}，實際累積出血約 ${Math.round(S.ebl)} mL（clerk 不一定知道）。
【action】accept_plan：clerk 清楚報告是產後大出血，講出出血量或生命徵象嚴重度，以及已做的處置，或請你過來。你回「我現在過去」並交代一兩件事。hang_up：完全沒評估就打來，叫他先去看。continue：其他。${S.trip ? "你已經在路上了，不需要再 accept_plan，用 continue。" : ""}
【urgency】只在 accept_plan 時填：依 clerk 在電話裡「傳達出來」的急迫程度與清楚程度判斷你會多趕（不是依真實狀況）。high：講出休克徵象或大量出血的具體數字，明確請你立刻來；medium：有講重點但不夠具體或不夠急；low：含糊、輕描淡寫、沒有數字。`,
        `In a medical education game you are playing the OB/GYN attending "Dr. Lin", taking a call in the middle of the night from the on-call clerk ${S.trip ? "(you are on your way in to the hospital right now)" : "(you are at home)"}. Few words, short sentences; uses questions to make the student think, in clipped clinical shorthand ("EBL?", "Did you feel the fundus?", "Have you gone through the 4 Ts?"). If the student does well, briefly approve and add one point that takes it further; if they miss something, press. Do not give the answer outright.
[TRUTH] Lin Yi-Chun, 30, induced at 34 weeks for preeclampsia, massive postpartum hemorrhage one hour after delivery: uterine atony (MgSO4 contributes) plus retained placenta. The patient has childhood asthma. Contraindications: no Methergine in preeclampsia; Carboprost not recommended with asthma.
[WHAT THE CLERK ACTUALLY KNOWS] (if they cite data beyond this, press them)
${known()}
[CURRENT STATUS] ${clock()}; actual ${vtxt(v)}, actual cumulative blood loss about ${Math.round(S.ebl)} mL (the clerk may not know this).
[action] accept_plan: the clerk clearly reports a postpartum hemorrhage, states the blood loss or how severe the vitals are, and what has been done, or asks you to come in. You reply "I'm on my way" and give one or two instructions. hang_up: they called without having assessed anything at all; tell them to go look first. continue: anything else. ${S.trip ? "You are already on your way; no need to accept_plan again, use continue." : ""}
[urgency] Fill in only with accept_plan: judge how fast you will hurry based on the urgency and clarity the clerk CONVEYS on the phone (not on the real situation). high: describes signs of shock or gives specific numbers for heavy bleeding, and clearly asks you to come right now; medium: covers the key points but is not specific or urgent enough; low: vague, plays it down, no numbers.`,
      ),
      extra: `"action":"continue","urgency":"medium"`,
      after(r) {
        if (r.action === "hang_up") {
          post("vs", "narr", null, tr("林主任掛斷了電話。", "Dr. Lin hangs up on you."));
          S.mood.vs = (S.mood.vs ?? 0) + 1;
        }
        if (r.action === "accept_plan" && !S.vsAccepted) {
          const urg: TripUrgency = isTripUrgency(r.urgency) ? r.urgency : "medium";
          const eta = { high: 6, medium: 11, low: 18 }[urg];
          S.vsAccepted = true;
          S.vsArriveAt = S.t + eta;
          startVsTrip(urg, eta);
          post("vs", "narr", null, tr("林主任掛了電話。", "Dr. Lin hangs up."));
        }
      },
      cost: incoming ? 0 : 2,
    },
    latest,
  );
}

/* ---------- dialogs ---------- */

const NURSE_ORDER_ALIAS: Record<string, string> = { massage: "nurse_massage", cbc: "lab_cbc", coag: "lab_coag" };

const dialogs: Level["dialogs"] = {
  bed: {
    title: tr("恢復室 1 床", "Recovery Bed 1"),
    sub: () =>
      S.husbandAway !== null
        ? tr("怡君（志明在外面）", "Yi-Chun (Chih-Ming is outside)")
        : tr("怡君和先生志明", "Yi-Chun and her husband Chih-Ming"),
    pics: () => ["yijun", ...(S.husbandAway !== null ? [] : ["husband"]), ...(S.nurseMassage ? ["meiling"] : [])],
    ch: "bed",
    talk: true,
    ph: () =>
      S.husbandAway !== null
        ? tr("對怡君說…", "Say something to Yi-Chun…")
        : tr("對怡君或志明說…", "Say something to Yi-Chun or Chih-Ming…"),
    choices() {
      const a = (id: string) => () => act(id);
      return [
        C(tr("床邊特寫", "Bedside close-up"), () => openZoom({ dlg: "bed", ...bedZoom() })),
        C(tr("看監視器", "Check monitor"), openMon),
        C(tr("看病人和護墊", "Look at her and the pads"), a("look")),
        C(tr("秤護墊估出血", "Weigh pads (EBL)"), a("weigh")),
        "sep",
        C(tr("摸子宮底", "Palpate fundus"), a("fundus")),
        C(tr("按摩子宮", "Massage uterus"), a("massage")),
        C(tr("雙手壓迫子宮（5 分鐘）", "Bimanual compression (5 min)"), a("bimanual")),
        "sep",
        C(tr("檢查產道裂傷", "Check for lacerations"), a("trauma")),
        C(tr("放導尿管", "Place Foley"), a("foley"), !S.bladderFull),
      ];
    },
    async onTalk(text) {
      const sp: Record<string, string> = { yijun: tr("怡君", "Yi-Chun") };
      if (S.husbandAway === null) sp.husband = tr("志明", "Chih-Ming");
      if (S.nurseMassage)
        sp.meiling = tr("美玲姐（在床邊按摩子宮）", "Mei-Ling (at the bedside, massaging the uterus)");
      await chat(text, {
        ch: "bed",
        think: "yijun",
        speakers: sp,
        body: tr(
          `你在醫學教育遊戲中同時扮演產房恢復室 1 床旁邊的人：產婦「林怡君」、先生「志明」${S.nurseMassage ? "，以及正在床邊按摩子宮的資深護理師「美玲姐」" : ""}。玩家是婦產科見習醫學生（clerk）。
怡君：30 歲，第一胎，34 週因子癲前症引產，一小時前自然產，寶寶在新生兒加護病房。累、怕、擔心寶寶。志明：32 歲工程師，很緊張，一直問「她會不會有事」，對太太病史比她清楚。${S.nurseMassage ? "美玲姐：冷靜、話少，只有在 clerk 問到護理或處置相關的事才插話，不替 clerk 下診斷。" : ""}
【隱藏真相，不能說出】產後大出血：子宮收縮不良加胎盤殘留。
【可揭露的事實】被問到才說，括號是 id：
${Object.entries(FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
asthma：怡君被問「有沒有其他病」可能想不起來，志明會補充；被直接問到氣喘或過去病史一定會說。
【狀態】${clock()}；怡君：${condition()}。狀況差時講話要短。${S.husbandAway !== null ? "志明現在在外面，不在床邊，不能說話。" : ""}已揭露：${[...S.revealed].join(",") || "無"}
【行動】act 只能是 none、husband_leave、husband_return。clerk 請志明先出去、到外面等時用 husband_leave（他會真的走出去）；請他回來時用 husband_return；其他 none。`,
          `In a medical education game you are playing everyone at Recovery Bed 1 in L&D at once: the patient "Lin Yi-Chun" and her husband "Chih-Ming"${S.nurseMassage ? ', plus the senior nurse "Mei-Ling", who is at the bedside massaging the uterus' : ""}. The player is an OB/GYN clerk (medical student on clerkship).
Yi-Chun: 30, first baby, induced at 34 weeks for preeclampsia, vaginal delivery an hour ago; the baby is in the NICU. Tired, scared, worried about the baby. Chih-Ming: 32-year-old engineer, very nervous, keeps asking "Is she going to be okay?", knows his wife's medical history better than she does.${S.nurseMassage ? " Mei-Ling: calm, says little; she only chimes in when the clerk asks about nursing care or management, and never makes the diagnosis for the clerk." : ""}
[HIDDEN TRUTH, NEVER SAY IT] Postpartum hemorrhage: uterine atony plus retained placenta.
[DISCLOSABLE FACTS] Share only when asked; the id is in parentheses:
${Object.entries(FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
asthma: if Yi-Chun is asked "any other medical problems?" she may not remember, and Chih-Ming will fill in; if asked directly about asthma or her past history, she will always say it.
[STATE] ${clock()}; Yi-Chun: ${condition()}. Keep her lines short when she is doing badly. ${S.husbandAway !== null ? "Chih-Ming is outside right now, not at the bedside, and cannot speak. " : ""}Revealed: ${[...S.revealed].join(",") || "none"}
[ACTIONS] act must be one of none, husband_leave, husband_return. Use husband_leave when the clerk asks Chih-Ming to step out and wait outside (he will actually walk out); husband_return when they ask him to come back; otherwise none.`,
        ),
        extra: `"revealed":[],"act":"none"`,
        after(r) {
          for (const id of strList(r.revealed)) if (isFact(id)) S.revealed.add(id);
          if (r.act === "husband_leave") husbandOut();
          else if (r.act === "husband_return") husbandIn(tr("志明回到床邊。", "Chih-Ming comes back to the bedside."));
        },
      });
    },
  },
  nurse: {
    title: tr("美玲姐", "Mei-Ling"),
    sub: tr("產房資深護理師", "Senior L&D nurse"),
    pics: ["meiling"],
    ch: "nurse",
    talk: true,
    ph: tr("對美玲姐說，也可以口頭下醫囑", "Talk to Mei-Ling, or give verbal orders"),
    choices() {
      return [
        C(tr("抽血：CBC", "Labs: CBC"), () => act("lab_cbc"), !!S.labs.cbc),
        C(tr("抽血：凝血＋纖維蛋白原", "Labs: coags + fibrinogen"), () => act("lab_coag"), !!S.labs.coag),
        C(tr("請她持續按摩子宮", "Ask her to keep massaging"), () => act("nurse_massage"), S.nurseMassage),
      ];
    },
    async onTalk(text) {
      const last = S.vitalsSeen.at(-1);
      await chat(text, {
        ch: "nurse",
        think: "meiling",
        speakers: { meiling: tr("美玲姐", "Mei-Ling") },
        cost: 1,
        body: tr(
          `你在醫學教育遊戲中扮演產房資深護理師「美玲姐」，產房二十年，冷靜、講話快，期待醫師主導。台式中英夾雜（「fundus 很軟」「要不要先 call R？」）。照醫師口頭醫囑做事，不替 clerk 下診斷；明顯不妥的醫囑會說「醫師你確定嗎？」。
【她知道的病歷】子癲前症合併嚴重特徵；MgSO4 1 g/hr 持續中；兒童期氣喘；胎盤娩出紀錄沒寫完不完整；產後 Oxytocin 已滴完。氣喘她不會主動說，除非 clerk 問病史或問她某藥能不能給。
【隱藏真相，不能說出】子宮收縮不良加胎盤殘留。
【現況】${clock()}；最近量到：${last ? `${vtxt(last.v)}（${clock(last.t)}）` : "還沒量"}
clerk 做過的事：
${known()}
【可執行的醫囑 id】massage 持續按摩子宮；oxytocin；methergine；carboprost；misoprostol；txa；calcium；iv2；o2；broncho；foley；cbc；coag；blood 備血；call_r2
clerk 明確要求的醫囑，把 id 放進 orders。要停 MgSO4 的話提醒「主任說要掛滿 24 小時」，不要放進 orders。不要編數字。`,
          `In a medical education game you are playing the senior L&D nurse "Mei-Ling": twenty years in L&D, calm, talks fast, expects the doctor to take charge. She talks in brisk clinical shorthand ("fundus is boggy", "want me to call the resident first?"). She carries out the doctor's verbal orders and never makes the diagnosis for the clerk; for a clearly inappropriate order she says "Doctor, are you sure?".
[WHAT SHE KNOWS FROM THE CHART] Preeclampsia with severe features; MgSO4 1 g/hr running; childhood asthma; the placental delivery note doesn't say whether it was complete; the postpartum oxytocin has finished. She won't bring up the asthma on her own unless the clerk asks about the history or asks her whether a particular drug is OK to give.
[HIDDEN TRUTH, NEVER SAY IT] Uterine atony plus retained placenta.
[CURRENT STATUS] ${clock()}; most recent reading: ${last ? `${vtxt(last.v)} (${clock(last.t)})` : "not measured yet"}
What the clerk has done:
${known()}
[ORDER IDS SHE CAN CARRY OUT] massage keep massaging the uterus; oxytocin; methergine; carboprost; misoprostol; txa; calcium; iv2; o2; broncho; foley; cbc; coag; blood type and crossmatch; call_r2
For orders the clerk explicitly gives, put the id in orders. If they want to stop the MgSO4, remind them "Dr. Lin said it runs the full 24 hours" and do not put it in orders. Do not make up numbers.`,
        ),
        extra: `"orders":[]`,
        after(r) {
          const orders: unknown[] = Array.isArray(r.orders) ? (r.orders as unknown[]) : [];
          for (const raw of orders) {
            const o = String(raw);
            const a = NURSE_ORDER_ALIAS[o] ?? o;
            const cur = S.dialog;
            if (isDrug(a)) give(a, "nurse");
            else act(a);
            if (S.dialog !== cur) {
              S.dialog = cur;
              renderSide();
            }
          }
        },
      });
    },
  },
  vs: {
    title: tr("林主任", "Dr. Lin"),
    sub: tr("電話中", "On the phone"),
    pics: ["vs"],
    ch: "vs",
    talk: true,
    ph: tr("向林主任報告…", "Report to Dr. Lin…"),
    keepOpen: true,
    async onTalk(text) {
      await vsTalk(text);
    },
    async onCall(reason) {
      await vsTalk("", reason);
    },
  },
  cart: {
    title: tr("藥車", "Med cart"),
    sub: tr("你決定，美玲姐幫你給", "You decide, Mei-Ling gives it"),
    ch: "cart",
    choices() {
      const g = (id: DrugId) => () => give(id, "cart");
      const a = (id: string) => () => act(id);
      return [
        C(
          tr("Oxytocin 20 IU 加入 LR 1 L 滴注", "Oxytocin 20 IU in 1 L LR, infusion"),
          g("oxytocin"),
          S.drugs.oxytocin != null,
        ),
        C("Methylergonovine 0.2 mg IM", g("methergine"), S.drugs.methergine != null),
        C("Carboprost 250 μg IM", g("carboprost"), S.drugs.carboprost != null),
        C(tr("Misoprostol 800 μg 塞肛", "Misoprostol 800 μg PR"), g("misoprostol"), S.drugs.misoprostol != null),
        C("Tranexamic acid 1 g IV", g("txa"), S.drugs.txa != null),
        "sep",
        C(tr("再打一條 16G，LR 全速", "Second 16G IV, LR wide open"), a("iv2"), S.fluidStart !== null),
        C(tr("氧氣面罩", "Oxygen mask"), a("o2"), S.o2),
        C(tr("支氣管擴張劑", "Bronchodilator"), a("broncho"), S.broncho),
        C("Calcium gluconate 1 g IV", g("calcium"), S.drugs.calcium != null),
      ];
    },
  },
  phone: {
    title: tr("護理站電話", "Nurses’ station phone"),
    sub: tr("要打給誰？", "Who are you calling?"),
    ch: "phone",
    choices() {
      return [
        C(
          tr("打給林主任", "Call Dr. Lin"),
          () => {
            S.vsTalked = true;
            openDialog("vs");
          },
          S.vsHere,
        ),
        C(tr("打給 R2 小周", "Call Chou (R2)"), () => act("call_r2"), S.r2CalledAt !== null),
        C(tr("打給血庫：備 2U PRBC", "Blood bank: 2U PRBC"), () => act("blood"), S.bloodReadyAt !== null),
      ];
    },
  },
  us: {
    title: tr("超音波機", "Ultrasound"),
    sub: tr("可以推到床邊", "Can be wheeled to the bedside"),
    ch: "us",
    choices() {
      return [C(tr("推到床邊照超音波", "Bedside scan"), () => act("us"), S.usDone)];
    },
  },
  chart: {
    title: tr("怡君的病歷", "Yi-Chun’s chart"),
    sub: tr("產前與生產紀錄", "Prenatal and delivery record"),
    ch: "chart",
    doc: doc(() => {
      S.chartRead = true;
      return [
        [
          tr("基本資料", "Basics"),
          tr("林怡君，30 歲，G1P1。血型 B 型 Rh(+)。", "Lin Yi-Chun, 30, G1P1. Blood type B, Rh(+)."),
        ],
        [
          tr("過去病史", "Past history"),
          tr(
            "兒童期氣喘，換季偶發作，最近一次發作兩年前，平時用吸入劑。無藥物過敏。",
            "Childhood asthma, occasional flares when the seasons change, last attack two years ago; uses an inhaler. No drug allergies.",
          ),
        ],
        [
          tr("產前", "Prenatal"),
          tr(
            "34+2 週，子癲前症合併嚴重特徵（BP 162/108、頭痛、尿蛋白 3+、PLT 92k），Labetalol 控制血壓。",
            "34+2 weeks, preeclampsia with severe features (BP 162/108, headache, urine protein 3+, PLT 92k); BP controlled with labetalol.",
          ),
        ],
        [
          tr("生產", "Delivery"),
          tr(
            "Oxytocin 引產，23:30 NSD，男嬰 2,150 g，轉新生兒加護病房。23:42 胎盤娩出（紀錄：「胎盤娩出」，未註明是否完整）。會陰二度裂傷已縫合。產時估計出血 300 mL。",
            "Oxytocin induction, NSD at 23:30, baby boy 2,150 g, transferred to the NICU. Placenta delivered at 23:42 (note reads “placenta delivered”; completeness not documented). Second-degree perineal laceration repaired. EBL at delivery 300 mL.",
          ),
        ],
        [
          tr("目前用藥", "Current meds"),
          tr(
            "MgSO4 1 g/hr 持續中（計畫產後 24 小時）。產後 Oxytocin 滴注已於 00:10 滴完。",
            "MgSO4 1 g/hr running (planned for 24 hours postpartum). Postpartum oxytocin infusion finished at 00:10.",
          ),
        ],
      ];
    }),
  },
};

/* ---------- level ---------- */

export const PPH: Level = {
  id: "pph",
  no: tr("四", "4"),
  short: tr("第四關　產房", "Level 4 · L&D"),
  title: tr("產房：產後一小時", "L&D: One Hour Postpartum"),
  place: tr("產房恢復室", "L&D recovery room"),
  blurb: tr(
    "第三關的怡君剛生完，護墊已經換了三片。",
    "Yi-Chun from Level 3 has just delivered, and she’s already soaked through three pads.",
  ),
  cast: ["yijun", "husband", "meiling", "vs", "r2"],
  intro: tr(
    [
      "週六凌晨 00:30。第三關的怡君收住院後引產，23:30 自然產下一個男寶寶，寶寶在新生兒加護病房。她現在在產房恢復室，先生志明陪著。",
      "你坐在護理站寫紀錄，美玲姐從恢復室探頭出來。",
    ],
    [
      "Saturday, 00:30. After Yi-Chun from Level 3 was admitted, she was induced and had a baby boy by vaginal delivery at 23:30; the baby is in the NICU. She’s now in the L&D recovery room with her husband Chih-Ming.",
      "You’re at the nurses’ station writing notes when Mei-Ling pokes her head out of the recovery room.",
    ],
  ),
  start: 30,
  maxT: 75,
  night: true,
  map: [
    "####################",
    "#aaaaaaaaa#bbbbbbbb#",
    "#aaaaaaaaa#bbbbbbbb#",
    "#aaaaaaaaa#bbbbbbbb#",
    "#aaaaaaaaa#bbbbbbbb#",
    "#aaaaaaaaaabbbbbbbb#",
    "#aaaaaaaaaabbbbbbbb#",
    "#aaaaaaaaa#bbbbbbbb#",
    "#aaaaaaaaa#bbbbbbbb#",
    "#aaaaaaaaa#bbbbbbbb#",
    "#aaaaaaaaa#bbbbbbbb#",
    "####################",
  ],
  zones: {
    a: ["#cfe3d6", "#bcd5c5", "#c4dccc"],
    b: ["#e9e1cf", "#d8cdb5", "#e0d6c0"],
  },
  windows: [
    [4, 0],
    [5, 0],
    [14, 0],
    [15, 0],
  ],
  exits: [DOOR],
  chName: {
    bed: tr("1 床", "Bed 1"),
    nurse: tr("美玲姐", "Mei-Ling"),
    vs: tr("林主任", "Dr. Lin"),
    cart: tr("藥車", "Med cart"),
    phone: tr("電話", "Phone"),
    us: tr("超音波", "Ultrasound"),
    sys: tr("現場", "L&D"),
  },
  objects: [
    { id: "monitor", type: "monitorStand", x: 2, y: 3, label: tr("監視器", "Monitor") },
    { id: "bed", type: "pphbed", x: 3, y: 3, h: 2, label: tr("恢復室 1 床：怡君", "Recovery Bed 1: Yi-Chun") },
    { id: "chair", type: "chair", x: 6, y: 2 },
    { id: "us", type: "us", x: 8, y: 2, label: tr("超音波機", "Ultrasound") },
    { id: "bed2", type: "bed", x: 3, y: 8, h: 2, label: tr("空床", "Empty bed") },
    { id: "sink", type: "sink", x: 1, y: 10, label: tr("洗手台", "Sink") },
    { id: "cart", type: "cart", x: 8, y: 9, label: tr("藥車", "Med cart") },
    { id: "shelf", type: "shelf", x: 12, y: 1, w: 6 },
    { id: "counter", type: "counter", x: 12, y: 2, w: 6 },
    { id: "phone", type: "phone", x: 12, y: 2, label: tr("電話", "Phone") },
    { id: "chart", type: "chart", x: 16, y: 2, label: tr("怡君的病歷", "Yi-Chun’s chart") },
    { id: "bench", type: "bench", x: 12, y: 9, w: 2 },
    { id: "plant", type: "plant", x: 18, y: 10 },
  ],
  labels: {
    bed: tr("1 床：怡君、志明", "Bed 1: Yi-Chun, Chih-Ming"),
    nurse: tr("美玲姐", "Mei-Ling"),
    r2tile: tr("小周", "Chou"),
    monitor: tr("監視器", "Monitor"),
  },
  help: {
    monitor: tr("生命徵象，會打開大螢幕", "Vitals; opens the big screen"),
    bed: tr("問診、摸子宮、看護墊（會打開特寫）", "History, uterus, pads (opens a close-up)"),
    us: tr("可以推到床邊照", "Can be wheeled to the bedside"),
    bed2: tr("空床", "Empty bed"),
    sink: tr("洗手台", "Sink"),
    cart: tr("子宮收縮劑、TXA、輸液、氧氣", "Uterotonics, TXA, fluids, oxygen"),
    phone: tr("打給林主任、R2 小周、血庫", "Call Dr. Lin, Chou (R2), blood bank"),
    chart: tr("產前、生產紀錄與過去病史", "Prenatal and delivery records, past history"),
    nurse: tr("抽血、口頭醫囑、請她按摩子宮", "Labs, verbal orders, uterine massage"),
    r2tile: tr("R2 小周", "Chou (R2)"),
  },
  helpRules: tr(
    [
      "出血一直在進行，越晚處理越難救。",
      "藥車上的藥由美玲姐幫你給。",
      "打給林主任時，你講得多急、多清楚，他就來得多快。左下角會顯示他到哪了。",
    ],
    [
      "She keeps bleeding. The longer you wait, the harder she is to save.",
      "Mei-Ling gives the drugs from the med cart for you.",
      "When you call Dr. Lin, the more urgent and clear you sound, the faster he gets here. The bottom-left corner shows where he is.",
    ],
  ),
  player: [14, 4],
  ents: () => [
    mkEnt({ k: "husband", id: "husband", x: 5, y: 3, act: "bed", face: "left" }),
    mkEnt({ k: "meiling", id: "meiling", x: 17, y: 5, act: "nurse" }),
  ],
  init: (): Vars => ({
    ebl: 700,
    tissue: true,
    bladderFull: true,
    massageUntil: -1,
    nurseMassage: false,
    bimanualUntil: -1,
    drugs: {},
    warned: {},
    fluidStart: null,
    bloodReadyAt: null,
    bloodGivenAt: null,
    o2: false,
    broncho: false,
    asthmaAt: null,
    asthmaPending: null,
    asthmaResolved: false,
    crisisAt: null,
    crisisPending: null,
    r2CalledAt: null,
    r2State: "none",
    r2Phase: 0,
    vsTalked: false,
    vsAccepted: false,
    vsArriveAt: null,
    vsCallAt: null,
    vsHere: false,
    shock: false,
    labs: {},
    vitalsSeen: [],
    eblSeen: [],
    chartRead: false,
    revealed: new Set(),
    stable: 0,
    alerts: {},
    usDone: false,
    trip: null,
    away: 0,
    husbandAway: null,
    bedAnim: null,
    bedGone: false,
    endAt: null,
  }),
  dialogs,
  zooms: { bed: bedZoom },
  quick: {
    monitor: openMon,
    bed2: () => note(tr("空床，床單摺得整整齊齊。", "An empty bed, sheets folded neatly.")),
    sink: () => note(tr("你洗了手。冷水讓你清醒一點。", "You wash your hands. The cold water wakes you up a little.")),
    r2tile: () =>
      toast({ ch: "bed", kind: "npc", who: "r2", text: tr("我在處理，你繼續。", "I’m on it. Keep going."), t: S.t }),
  },

  onStart: () => eventBanner(tr("產後一小時", "One hour postpartum"), tr("恢復室", "Recovery room")),
  tick(t) {
    S.ebl += rate();
    const def = deficit();
    for (const [k, l] of Object.entries(S.labs) as ["cbc" | "coag", Lab][]) {
      if (!l.done && t >= l.ready) {
        l.done = true;
        const txt =
          k === "cbc"
            ? tr(
                `CBC：Hb ${(11.6 - Math.max(0, (l.def ?? 0) - 500) / 380).toFixed(1)} g/dL（急性出血時 Hb 會落後），PLT 98k，WBC 15,600`,
                `CBC: Hb ${(11.6 - Math.max(0, (l.def ?? 0) - 500) / 380).toFixed(1)} g/dL (Hb lags behind in acute bleeding), PLT 98k, WBC 15,600`,
              )
            : tr(
                "凝血：PT 11.8 秒，aPTT 31 秒，fibrinogen 245 mg/dL",
                "Coags: PT 11.8 s, aPTT 31 s, fibrinogen 245 mg/dL",
              );
        finding(k === "cbc" ? "CBC" : tr("凝血", "Coags"), txt);
        post("nurse", "result", "meiling", txt);
      }
    }
    if (S.bloodReadyAt !== null && S.bloodGivenAt === null && t >= S.bloodReadyAt) {
      S.bloodGivenAt = t;
      post("nurse", "npc", "meiling", tr("血來了，2U 我先掛上去。", "Blood’s here. I’m hanging the 2 units now."));
    }
    if (S.crisisPending === t) {
      S.crisisAt = t;
      S.crisisPending = null;
      const v = vitals();
      S.vitalsSeen.push({ t, v });
      eventBanner(tr("血壓飆升", "BP spike"), `BP ${v.sbp}/${v.dbp}`, true);
      post(
        "nurse",
        "alert",
        "meiling",
        tr(
          `醫師！她說頭突然很痛、眼睛霧霧的，血壓 ${v.sbp}/${v.dbp}！`,
          `Doctor! She says she suddenly has a terrible headache and her vision is hazy. BP ${v.sbp}/${v.dbp}!`,
        ),
      );
      if (!S.vsAccepted) S.vsCallAt = t + 2;
    }
    if (S.asthmaPending === t) {
      S.asthmaPending = null;
      S.asthmaAt = t;
      const v = vitals();
      S.vitalsSeen.push({ t, v });
      eventBanner(tr("支氣管痙攣", "Bronchospasm"), `SpO₂ ${v.spo2}%`, true);
      post(
        "nurse",
        "alert",
        "meiling",
        tr(
          `醫師，她在喘，聽得到咻咻聲，SpO₂ 掉到 ${v.spo2}！`,
          `Doctor, she’s short of breath, I can hear her wheezing. SpO₂ is down to ${v.spo2}!`,
        ),
      );
      addWait({
        id: "wheeze",
        text: tr("怡君在喘", "Yi-Chun is wheezing"),
        urgent: true,
        icon: tr("！", "!"),
        clearOn: "manual",
      });
    }
    if (S.asthmaAt !== null && !S.asthmaResolved && S.o2 && S.broncho && t >= S.asthmaAt + 6) {
      S.asthmaResolved = true;
      clearWait("wheeze");
      post("nurse", "npc", "meiling", tr("喘比較好了，SpO₂ 回到 96。", "Her breathing’s better. SpO₂ back up to 96."));
    }
    const alertAt = (k: string, cond: boolean, text: (v: Vitals) => string): void => {
      if (cond && !S.alerts[k]) {
        S.alerts[k] = true;
        const v = vitals();
        S.vitalsSeen.push({ t, v });
        post("nurse", "alert", "meiling", text(v));
      }
    };
    if (t === 1) {
      alertAt("start", true, () =>
        tr(
          "醫師，1 床怡君產後一個小時，護墊已經換三片了，你要不要去看一下？",
          "Doctor, Yi-Chun in Bed 1 is an hour postpartum and she’s already soaked through three pads. Can you go take a look?",
        ),
      );
      addWait({
        id: "go",
        text: tr("美玲姐：去看 1 床", "Mei-Ling: go see Bed 1"),
        dlg: "bed",
        clearOn: "open",
        icon: tr("！", "!"),
      });
    }
    alertAt("d1200", def >= 1200 && !S.shock, (v) =>
      tr(
        `醫師，她血壓掉到 ${v.sbp}/${v.dbp} 了，心跳 ${v.hr}。她平常都 150 耶。`,
        `Doctor, her BP’s dropped to ${v.sbp}/${v.dbp}, heart rate ${v.hr}. She’s usually around 150!`,
      ),
    );
    alertAt("d1700", def >= 1700 && !S.shock, (v) =>
      tr(
        `醫師！BP ${v.sbp}/${v.dbp}，HR ${v.hr}，她講話越來越慢了！`,
        `Doctor! BP ${v.sbp}/${v.dbp}, HR ${v.hr}, and she’s talking slower and slower!`,
      ),
    );
    if (t === 9 && S.r2CalledAt === null && !S.vsTalked)
      ask("nurse", "meiling", tr("醫師，要不要先叫人？", "Doctor, shouldn’t we call for help?"));
    if (S.husbandAway !== null && t === S.husbandAway + 12)
      husbandIn(
        tr("志明在外面待不住，又走回床邊。", "Chih-Ming can’t stand waiting outside and comes back to the bedside."),
      );
    const far = Math.abs(P.x - 3) + Math.abs(P.y - 4) > 5;
    if (def >= 1200 && !S.shock && far) S.away++;
    else S.away = 0;
    if (S.away === 5 && !S.alerts.away && S.husbandAway === null) {
      S.alerts.away = true;
      post(
        "bed",
        "alert",
        "husband",
        tr("醫生！她一直在發抖，你去哪裡了！", "Doctor! She won’t stop shaking. Where did you go?!"),
      );
    }
    if (S.r2State === "coming" && S.r2CalledAt !== null && t >= S.r2CalledAt + 6) {
      S.r2State = "here";
      S.r2Phase = t;
      spawnAt("r2", "r2tile", DOOR, [2, 5]);
      post(
        "bed",
        "npc",
        "r2",
        tr("我來了。主任知道了嗎？我先看一下。", "I’m here. Does Dr. Lin know? Let me take a look first."),
      );
    }
    if (S.r2State === "here" && t === S.r2Phase + 3 && !S.usDone) {
      S.usDone = true;
      const txt = S.tissue
        ? tr(
            "子宮腔內約 3 cm 高回音組織，像胎盤殘留。",
            "About 3 cm of echogenic tissue in the uterine cavity, looks like retained placenta.",
          )
        : tr("子宮腔內乾淨。", "Uterine cavity is empty.");
      finding(tr("R2 超音波", "R2 ultrasound"), txt);
      post("bed", "result", "r2", tr(`超音波：${txt}`, `Ultrasound: ${txt}`));
    }
    if (S.r2State === "here" && t === S.r2Phase + 6 && S.tissue) {
      S.tissue = false;
      finding(
        tr("R2 處置", "R2 procedure"),
        tr("手取出約 3 cm 胎盤組織", "Manually removed about 3 cm of placental tissue"),
      );
      post(
        "bed",
        "npc",
        "r2",
        tr(
          "裡面有一塊胎盤，我清出來了，大概三公分。子宮比較會縮了。",
          "There was a piece of placenta in there. I got it out, about three centimeters. The uterus is contracting better now.",
        ),
      );
    }
    if (!S.shock && def >= 2200) {
      S.shock = true;
      const v = vitals();
      S.vitalsSeen.push({ t, v });
      eventBanner(
        tr("出血性休克", "Hemorrhagic shock"),
        tr(`BP ${v.sbp}/${v.dbp}　HR ${v.hr}`, `BP ${v.sbp}/${v.dbp} · HR ${v.hr}`),
        true,
      );
      post(
        "nurse",
        "alert",
        "meiling",
        tr(
          `醫師！她叫不太醒了！BP ${v.sbp}/${v.dbp}，HR ${v.hr}！我直接 call 主任了！`,
          `Doctor! I can barely rouse her! BP ${v.sbp}/${v.dbp}, HR ${v.hr}! I’m calling Dr. Lin myself!`,
        ),
      );
      if (!S.vsAccepted || (S.vsArriveAt ?? 0) - t > 5) {
        S.vsAccepted = true;
        S.vsArriveAt = t + 5;
        startVsTrip("high", 5);
      }
    }
    if (S.trip && !S.trip.cb && S.trip.urg !== "high" && t - S.trip.start >= Math.floor(S.trip.eta / 2)) {
      S.trip.cb = true;
      incomingCall("cb", "vs");
    }
    if (S.vsCallAt === t && !S.vsAccepted) {
      S.vsCallAt = null;
      incomingCall("crisis", "vs");
    }
    if (S.vsArriveAt === t) {
      S.vsHere = true;
      pipSet(null);
      spawnAt("vs", "bed", DOOR, [4, 6]);
      eventBanner(tr("林主任到場", "Dr. Lin arrives"));
      post(
        "bed",
        "npc",
        "vs",
        S.shock
          ? tr("推進開刀房，通知麻醉，啟動大量輸血。", "To the OR. Call anesthesia. Activate massive transfusion.")
          : tr("我來了。現在怎樣？出血量多少？", "I’m here. Where are we? How much blood loss?"),
      );
      S.endAt = t + 2;
    }
    if (S.endAt === t) {
      if (S.shock) endC();
      else endB();
    }
    if (!S.tissue && rate() < 5 && !S.shock) {
      S.stable++;
      if (S.stable === 6 && !S.alerts.ok && !S.vsHere) {
        S.alerts.ok = true;
        eventBanner(tr("出血控制", "Bleeding controlled"));
        endA();
      }
    } else S.stable = 0;
  },
  timeup: () =>
    void playEnding(
      "Z",
      tr("交班", "Sign-out"),
      [
        {
          fn: () => spawnAt("r2", "r2tile", DOOR, [2, 5]),
          text: tr(
            "天快亮了，白班的小周走進恢復室。",
            "It’s almost dawn. Chou, on the day shift, walks into the recovery room.",
          ),
          wait: 3000,
        },
        { who: "r2", text: tr("我接了。你回去睡吧。", "I’ve got it. Go get some sleep.") },
      ],
      tr("時間到，交班給白班。", "Time’s up. Signed out to the day team."),
    ),

  hud() {
    const last = S.vitalsSeen.at(-1);
    const eb = S.eblSeen.at(-1);
    return [
      [
        last ? tr(`${clock(last.t)} 生命徵象`, `Vitals at ${clock(last.t)}`) : tr("生命徵象", "Vitals"),
        last
          ? tr(
              `${last.v.sbp}/${last.v.dbp}　HR ${last.v.hr}　SpO₂ ${last.v.spo2}`,
              `${last.v.sbp}/${last.v.dbp} · HR ${last.v.hr} · SpO₂ ${last.v.spo2}`,
            )
          : tr("還沒看", "Not checked"),
      ],
      [
        eb ? tr(`${clock(eb.t)} 估計出血`, `EBL at ${clock(eb.t)}`) : tr("估計出血", "EBL"),
        eb ? `${eb.v} mL` : tr("還沒秤", "Not weighed"),
      ],
    ];
  },
  urgent: () => deficit() >= 1200,
  outcomeText: () =>
    S.shock
      ? tr("病人休克，主治接手", "Patient in shock, attending took over")
      : tr("主治到場接手", "Attending arrived and took over"),

  drawBelow(c, now) {
    const px = 2 * T;
    const py = 3 * T;
    c.fillStyle = "#8a90a2";
    c.fillRect(px + 7, py + 6, 2, 10);
    c.fillStyle = "#3a3f52";
    c.fillRect(px + 1, py, 14, 9);
    c.fillStyle = "#0f1f1a";
    c.fillRect(px + 2, py + 1, 12, 7);
    const fast = deficit() > 1200;
    c.fillStyle = "#5be38a";
    for (let i = 0; i < 12; i++) {
      const ph = (i + Math.floor(now / (fast ? 60 : 110))) % 12;
      c.fillRect(px + 2 + i, ph === 3 ? py + 2 : ph === 4 ? py + 6 : py + 4, 1, 1);
    }
    if (Math.floor(now / 400) % 2 && fast) {
      c.fillStyle = "#ff4d5e";
      c.fillRect(px + 12, py + 1, 2, 2);
    }
    c.fillStyle = "#e6e9f5";
    c.fillRect(px + 13, py - 3, 3, 5);
    if (!S.bedGone) {
      const p = bedPos(now);
      if (p)
        DRAW.bed?.(c, Math.round(p[0] * T), Math.round(p[1] * T), {
          pt: true,
          hair: "#3a2a22",
          skin: deficit() > 1500 ? "#e9e2de" : "#f4dccd",
          sheet: "#a9c8e8",
        });
    }
  },

  graderCase: tr(
    `病例：30 歲初產婦，34 週子癲前症引產，產後一小時大出血。真相：子宮收縮不良（MgSO4 有影響、膀胱脹）加上胎盤殘留。病歷上有兒童期氣喘。Methergine 是子癲前症禁忌（護理師會提醒一次）；Carboprost 在氣喘病人會誘發支氣管痙攣（沒人提醒，要自己讀病歷或問）。主任趕來的速度取決於 clerk 電話裡傳達的急迫性。`,
    `Case: 30-year-old primigravida induced at 34 weeks for preeclampsia, massive postpartum hemorrhage one hour after delivery. Truth: uterine atony (MgSO4 contributes, plus a distended bladder) plus retained placenta. The chart documents childhood asthma. Methergine is contraindicated in preeclampsia (the nurse warns once); Carboprost triggers bronchospasm in an asthmatic patient (nobody warns; the clerk has to read the chart or ask). How fast the attending comes in depends on the urgency the clerk conveys on the phone.`,
  ),
  rubric: tr(
    [
      "及早求援：叫 R2 或主治的時機",
      "第一線：子宮按摩或雙手壓迫、Oxytocin",
      "量化與監測：秤出血、看生命徵象，理解子癲前症病人的基準血壓偏高",
      "4T：Tone（含膀胱）、Trauma、Tissue、Thrombin",
      "第二線藥物與禁忌：避開 Methergine 與 Carboprost，使用 TXA、Misoprostol",
      "復甦：第二條 IV、輸液、備血、氧氣",
      "溝通：向主治報告（急迫性是否傳達清楚）與對家屬說明",
    ],
    [
      "Calling for help early: when the R2 or the attending was called",
      "First line: uterine massage or bimanual compression, oxytocin",
      "Quantifying and monitoring: weighing blood loss, checking vitals, understanding that a preeclamptic patient's baseline BP runs high",
      "4 Ts: Tone (including the bladder), Trauma, Tissue, Thrombin",
      "Second-line drugs and contraindications: avoiding Methergine and Carboprost, using TXA and misoprostol",
      "Resuscitation: second IV, fluids, blood ordered, oxygen",
      "Communication: presentation to the attending (was the urgency conveyed clearly) and explaining to the family",
    ],
  ),
  facts: () => [
    tr(`最終累積出血約 ${eblRounded()} mL`, `Final cumulative blood loss about ${eblRounded()} mL`),
    tr(
      `叫 R2：${S.r2CalledAt !== null ? clock(S.r2CalledAt) : "沒有"}`,
      `Called R2: ${S.r2CalledAt !== null ? clock(S.r2CalledAt) : "no"}`,
    ),
    tr(
      `主任趕來：${S.trip ? { high: "很急", medium: "普通", low: "不急" }[S.trip.urg] : "沒有叫"}`,
      `Attending's trip in: ${S.trip ? { high: "urgent", medium: "normal", low: "unhurried" }[S.trip.urg] : "never called"}`,
    ),
    tr(
      `Oxytocin：${S.drugs.oxytocin != null ? clock(S.drugs.oxytocin) : "沒給"}`,
      `Oxytocin: ${S.drugs.oxytocin != null ? clock(S.drugs.oxytocin) : "not given"}`,
    ),
    tr(
      `Methergine：${S.drugs.methergine != null ? "給了（禁忌）" : "沒給"}`,
      `Methergine: ${S.drugs.methergine != null ? "given (contraindicated)" : "not given"}`,
    ),
    tr(
      `Carboprost：${S.drugs.carboprost != null ? "給了（氣喘）" : "沒給"}`,
      `Carboprost: ${S.drugs.carboprost != null ? "given (asthma)" : "not given"}`,
    ),
    tr(`病歷：${S.chartRead ? "讀過" : "沒讀"}`, `Chart: ${S.chartRead ? "read" : "not read"}`),
    tr(`休克：${S.shock ? "是" : "否"}`, `Shock: ${S.shock ? "yes" : "no"}`),
  ],
  known,
};
